import React, { useState, useContext } from 'react';
import _get from 'lodash/get';
import { useLocation } from 'react-router';
import useAnalytics from 'helpers/useAnalytics';
import MappingsContext from 'helpers/MappingsContext';
import * as MappingTypes from 'types/Mapping';
import { CSV_SOURCE_TYPE_KEY, MAP_ATTRIBUTES_URL, CSV_WIZARD_URL } from 'pages/Directory/consts';

// Components
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';
import { defineStyles, Classes } from 'styles/defineStyles';
import classNames from 'classnames';

const styles = defineStyles(theme => {
  const ITEM_HEIGHT = 48;

  return {
    row: {
      padding: theme.spacing(1, 1, 0, 1),
    },
    dividerLine: {
      height: 1,
      backgroundColor: theme.palette.text.secondary,
      margin: theme.spacing(),
      marginTop: 0,
    },
    menuListRoot: {
      maxHeight: ITEM_HEIGHT * 5.5,
      width: 400,
      overflowY: 'auto',
    },
    searchInput: {
      width: '100%',
    },
    subField: {
      marginLeft: theme.spacing(3),
    },
    addProperty: {
      fontWeight: theme.typography.body1.fontWeight,
      textTransform: 'capitalize',
      paddingLeft: theme.spacing(3),
      paddingBottom: theme.spacing(1.75),
    },
    title: {
      ...theme.typography.h5Semibold,
    },
    textField: {
      width: '100%',
    },
  };
});

interface Props {
  onSelectAttribute: (attribute: any) => void;
  classes: Classes<typeof styles>;
  search: string;
  newPropValue: string | null;
  menuItems: string[];
  onSearch: (e: any) => void;
  onSetNewProp: (value: string | null) => void;
  onSaveCustomProps: () => void;
}

const AttributeMenu = (props: Props) => {
  const {
    classes,
    onSelectAttribute,
    onSetNewProp,
    newPropValue,
    onSaveCustomProps,
    onSearch,
    menuItems,
  } = props;
  const analytics = useAnalytics();
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { source } = mappingsContext;
  const [addProp, setAddProp] = useState(false);
  const csvOptOut = _get(source, ['setupProgress', 'csvOptOut'], false);
  const location = useLocation();
  const { pathname } = location;
  const csvJustUploaded =
    pathname.includes(MAP_ATTRIBUTES_URL) || pathname.includes(CSV_WIZARD_URL);
  const shouldAllowAddCustomProperty = !(
    csvJustUploaded &&
    source.type === CSV_SOURCE_TYPE_KEY &&
    !csvOptOut
  );

  const handleOpen = () => {
    setAddProp(true);
  };

  const handleClose = () => {
    setAddProp(false);
  };

  const handleEditProp = (e: any) => {
    onSetNewProp(e.target.value);
  };

  const handleCancel = () => {
    handleClose();
    onSetNewProp(null);
  };

  const handleSave = () => {
    onSaveCustomProps();
    handleClose();
    analytics.track('Mapping Property Created', {
      category: 'AD - Directories',
      label: source.type,
    });
  };

  return (
    <>
      <div>
        <div className={classes.row}>
          <TextField className={classes.searchInput} placeholder="Search" onChange={onSearch} />
        </div>
        <MenuList className={classNames(classes.row, classes.menuListRoot)}>
          {menuItems.map((attribute: any, index: number) => {
            let secondaryText = '';

            return (
              <MenuItem
                onClick={e => onSelectAttribute(attribute)}
                key={index}
                data-cy={`${attribute}-primary-key-menu-item`}
              >
                <ListItemText
                  className={attribute.fromAttribute ? classes.subField : ''}
                  primary={attribute}
                  secondary={secondaryText}
                />
              </MenuItem>
            );
          })}
        </MenuList>
        {shouldAllowAddCustomProperty && (
          <>
            <div className={classes.dividerLine} />
            <Button
              className={classes.addProperty}
              onClick={handleOpen}
              data-cy="another-property-button"
            >
              Use another property...
            </Button>
          </>
        )}
      </div>
      <Dialog onClose={handleClose} open={addProp}>
        <DialogTitle disableTypography className={classes.title}>
          Use Another Property
        </DialogTitle>
        <DialogContent>
          <Typography>
            If there’s a property coming from your source that is not present in the provided
            dropdown, you can add it manually below. For example, this could happen if you have a
            CSV header that will be added to your file later, or if your objects coming from Azure
            AD have dynamic custom properties. Note: For API based sources, Sift supports dot
            notation for inner properties (ex: name.givenName).
          </Typography>
          <TextField
            className={classes.textField}
            label="Enter property name"
            value={newPropValue}
            onChange={handleEditProp}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCancel}>
            Cancel
          </Button>
          <Button autoFocus onClick={handleSave} color="primary" disabled={!newPropValue}>
            Create Property
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withStyles(styles)(AttributeMenu);
